import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PublicShell from "@/layouts/PublicShell";
import SEO from "@/components/SEO";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Send, Rocket, Check, Clock, AlertTriangle, ArrowLeft, Users,
  MapPin, Award, Compass, Sparkles,
} from "lucide-react";

// Status → i18n key + visual tone. Translating keys live inside components
// via useTranslation so switcher changes update the badge immediately.
const APPLY_STATUS_META = {
  submitted:       { i18n: "thanks.statusSubmitted",     tone: "border-[#E8D6A3]/40 bg-[#E8D6A3]/[0.05] text-[#E8D6A3]", icon: Clock },
  triage:          { i18n: "thanks.statusTriage",        tone: "border-[#E8D6A3]/40 bg-[#E8D6A3]/[0.05] text-[#E8D6A3]", icon: Clock },
  discovery:       { i18n: "thanks.statusDiscovery",     tone: "border-[#00E5FF]/40 bg-[#00E5FF]/[0.05] text-[#00E5FF]", icon: Clock },
  due_diligence:   { i18n: "thanks.statusDueDiligence",  tone: "border-[#00E5FF]/40 bg-[#00E5FF]/[0.05] text-[#00E5FF]", icon: Clock },
  decision_review: { i18n: "thanks.statusDecision",      tone: "border-[#D4B878]/40 bg-[#D4B878]/[0.05] text-[#D4B878]", icon: Clock },
  approved:        { i18n: "thanks.statusApproved",      tone: "border-[#CCFF00]/40 bg-[#CCFF00]/[0.05] text-[#CCFF00]", icon: Check },
  active:          { i18n: "thanks.statusActive",        tone: "border-[#CCFF00]/40 bg-[#CCFF00]/[0.05] text-[#CCFF00]", icon: Check },
  info_requested:  { i18n: "thanks.statusInfo",          tone: "border-[#E8D6A3]/40 bg-[#E8D6A3]/[0.05] text-[#E8D6A3]", icon: AlertTriangle },
  waitlisted:      { i18n: "thanks.statusWaitlisted",    tone: "border-white/15 bg-white/[0.03] text-white/70",         icon: Clock },
  declined:        { i18n: "thanks.statusDeclined",      tone: "border-red-400/40 bg-red-500/[0.05] text-red-200",      icon: AlertTriangle },
};

/* =================================================================
 * /partners/apply — public application form
 * ================================================================= */
export function PartnersApply() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState({
    kind: "individual",
    name: "",
    email: "",
    phone: "",
    country: "",
    city: "",
    legal_business_name: "",
    business_registration: "",
    categories: [],
    target_markets: "",
    experience_summary: "",
    skills: "",
    operational_capacity: "",
    existing_customers: "",
    portfolio_url: "",
    references: "",
    conflict_disclosure: "",
    want_founding_slot: params.get("founding") === "1",
    opt_in_public_profile: false,
    accepted_terms: false,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/partners/meta").then((r) => setMeta(r.data)).catch(() => setMeta({}));
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const toggleCategory = (cid) =>
    setForm((f) => ({
      ...f,
      categories: f.categories.includes(cid)
        ? f.categories.filter((x) => x !== cid)
        : [...f.categories, cid],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!form.accepted_terms) {
      toast.error(t("apply.errAcceptTerms"));
      return;
    }
    if (form.categories.length === 0) {
      toast.error(t("apply.errPickCategory"));
      return;
    }
    setBusy(true);
    try {
      // Serialize comma-ish fields into arrays only when they have content.
      const payload = { ...form };
      ["skills", "target_markets"].forEach((k) => {
        payload[k] = String(form[k] || "")
          .split(/[,\n;]+/)
          .map((s) => s.trim())
          .filter(Boolean);
      });
      ["phone", "city", "legal_business_name", "business_registration",
       "experience_summary", "operational_capacity", "existing_customers",
       "portfolio_url", "references", "conflict_disclosure"].forEach((k) => {
        if (!payload[k]) delete payload[k];
      });
      const r = await api.post("/partners/apply", payload);
      // Attach referral if present in query string — silently ignores
      // bad codes so a typo never blocks a valid application.
      const refCode = params.get("ref");
      if (refCode && r.data?.application_id) {
        api.post("/partners/applications/attach-referral", {
          application_id: r.data.application_id,
          referral_code: refCode,
        }).catch(() => {});
      }
      if (r.data?.deduplicated) {
        toast(t("apply.okDedup", { status: r.data.status }));
      } else {
        toast.success(t("apply.okSubmitted"));
      }
      navigate(`/partners/thanks/${r.data.application_id}`);
    } catch (err) {
      const raw = err?.response?.data?.detail;
      let msg = t("apply.errGeneric");
      if (typeof raw === "string") msg = raw;
      else if (Array.isArray(raw) && raw.length) {
        msg = raw.map((r) => (r?.msg ? `${(r.loc || []).slice(-1)[0] || "field"}: ${r.msg}` : String(r))).join(" · ");
      }
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const categories = meta?.categories || [];

  return (
    <PublicShell>
      <SEO
        title="Apply — Mirrorloops Partner Network"
        description="Apply to join the Mirrorloops Global Partner Network. Independent businesses, transparent commercials, real revenue."
        path="/partners/apply"
      />
      <div
        data-testid="partners-apply"
        className="max-w-4xl mx-auto px-6 pt-32 pb-24"
      >
        <div className="text-[11px] text-white/40 mb-4 font-mono">
          <Link to="/partners" className="hover:text-white">{t("apply.crumbPartners")}</Link>
          <span className="mx-2">/</span>
          <span className="text-white/70">{t("apply.crumbApply")}</span>
        </div>

        <div className="mb-10">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#E8D6A3] mb-2 flex items-center gap-2">
            <Sparkles size={12} /> {t("apply.eyebrow")}
          </div>
          <h1 className="text-3xl sm:text-5xl font-semibold text-white tracking-tight">
            {t("apply.title")}
          </h1>
          <p className="mt-3 text-white/60 max-w-2xl">
            {t("apply.sub")}
          </p>
          {params.get("ref") && (
            <div data-testid="apply-referral-badge" className="mt-4 inline-flex items-center gap-2 text-[11px] font-mono border border-[#D4B878]/40 bg-[#D4B878]/[0.06] text-[#E8D6A3] rounded-full px-3 py-1">
              <Sparkles size={11}/> {t("apply.referralBadge", { code: params.get("ref") })}
            </div>
          )}
        </div>

        <form onSubmit={submit} className="space-y-7">
          {/* Kind */}
          <section className="border border-white/10 rounded p-5 bg-white/[0.02]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">
              {t("apply.sectionKind")}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: "individual", label: t("apply.kindIndividual") },
                { v: "company", label: t("apply.kindCompany") },
              ].map((k) => (
                <button
                  key={k.v}
                  type="button"
                  onClick={() => setForm({ ...form, kind: k.v })}
                  data-testid={`apply-kind-${k.v}`}
                  className={`text-left rounded border px-4 py-3 transition ${
                    form.kind === k.v
                      ? "border-[#D4B878]/60 bg-[#D4B878]/[0.08] text-white"
                      : "border-white/10 bg-white/[0.02] text-white/60 hover:border-white/25"
                  }`}
                >
                  <div className="text-sm font-semibold">{k.label}</div>
                </button>
              ))}
            </div>
          </section>

          {/* Identity */}
          <section className="border border-white/10 rounded p-5 bg-white/[0.02] space-y-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">
              {t("apply.sectionIdentity")}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="apply-name" required placeholder={t("apply.fullName")}
                     value={form.name} onChange={set("name")} className="bg-white/5 border-white/10" />
              <Input data-testid="apply-email" required type="email" placeholder={t("apply.email")}
                     value={form.email} onChange={set("email")} className="bg-white/5 border-white/10" />
              <Input data-testid="apply-phone" placeholder={t("apply.phone")}
                     value={form.phone} onChange={set("phone")} className="bg-white/5 border-white/10" />
              <Input data-testid="apply-country" required placeholder={t("apply.country")}
                     value={form.country} onChange={set("country")} className="bg-white/5 border-white/10" />
              <Input data-testid="apply-city" placeholder={t("apply.city")}
                     value={form.city} onChange={set("city")} className="bg-white/5 border-white/10" />
            </div>
            {form.kind === "company" && (
              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-white/5">
                <Input data-testid="apply-legal-name" placeholder={t("apply.legalName")}
                       value={form.legal_business_name} onChange={set("legal_business_name")} className="bg-white/5 border-white/10" />
                <Input data-testid="apply-reg" placeholder={t("apply.registration")}
                       value={form.business_registration} onChange={set("business_registration")} className="bg-white/5 border-white/10" />
              </div>
            )}
          </section>

          {/* Categories */}
          <section className="border border-white/10 rounded p-5 bg-white/[0.02]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">
              {t("apply.sectionLanes")} <span className="text-[#D4B878]">{t("apply.sectionLanesHint")}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {categories.map((c) => {
                const picked = form.categories.includes(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggleCategory(c.id)}
                    data-testid={`apply-category-${c.id}`}
                    className={`text-left rounded border p-3 transition ${
                      picked
                        ? "border-[#D4B878]/60 bg-[#D4B878]/[0.08] text-white"
                        : "border-white/10 bg-white/[0.02] text-white/60 hover:border-white/25"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-semibold">{c.label}</div>
                      {picked && <Check size={13} className="text-[#CCFF00]" />}
                    </div>
                    <div className="text-[11px] text-white/50 mt-1 leading-snug">
                      {c.tagline}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Experience */}
          <section className="border border-white/10 rounded p-5 bg-white/[0.02] space-y-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">
              {t("apply.sectionExperience")}
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">
                {t("apply.experienceLabel")}
              </Label>
              <textarea data-testid="apply-exp" rows={3} value={form.experience_summary} onChange={set("experience_summary")}
                className="w-full mt-1 bg-white/5 border border-white/10 rounded p-2 text-sm text-white placeholder:text-white/30 resize-none"
                placeholder={t("apply.experiencePlaceholder")} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="apply-skills" placeholder={t("apply.skills")}
                     value={form.skills} onChange={set("skills")} className="bg-white/5 border-white/10" />
              <Input data-testid="apply-markets" placeholder={t("apply.targetMarkets")}
                     value={form.target_markets} onChange={set("target_markets")} className="bg-white/5 border-white/10" />
              <Input data-testid="apply-portfolio" placeholder={t("apply.portfolio")}
                     value={form.portfolio_url} onChange={set("portfolio_url")} className="bg-white/5 border-white/10" />
              <Input data-testid="apply-capacity" placeholder={t("apply.capacity")}
                     value={form.operational_capacity} onChange={set("operational_capacity")} className="bg-white/5 border-white/10" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">
                {t("apply.customersLabel")}
              </Label>
              <textarea data-testid="apply-customers" rows={2} value={form.existing_customers} onChange={set("existing_customers")}
                className="w-full mt-1 bg-white/5 border border-white/10 rounded p-2 text-sm text-white placeholder:text-white/30 resize-none" />
            </div>
          </section>

          {/* Governance */}
          <section className="border border-white/10 rounded p-5 bg-white/[0.02] space-y-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">
              {t("apply.sectionGovernance")}
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">
                {t("apply.referencesLabel")}
              </Label>
              <textarea data-testid="apply-refs" rows={2} value={form.references} onChange={set("references")}
                className="w-full mt-1 bg-white/5 border border-white/10 rounded p-2 text-sm text-white placeholder:text-white/30 resize-none" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">
                {t("apply.conflictLabel")}
              </Label>
              <textarea data-testid="apply-conflict" rows={2} value={form.conflict_disclosure} onChange={set("conflict_disclosure")}
                className="w-full mt-1 bg-white/5 border border-white/10 rounded p-2 text-sm text-white placeholder:text-white/30 resize-none"
                placeholder={t("apply.conflictPlaceholder")} />
            </div>
          </section>

          {/* Flags + submit */}
          <section className="border border-white/10 rounded p-5 bg-white/[0.02] space-y-3">
            <label className="flex items-start gap-2 cursor-pointer">
              <Checkbox data-testid="apply-founding" checked={form.want_founding_slot}
                        onCheckedChange={(v) => setForm({ ...form, want_founding_slot: !!v })} />
              <div>
                <div className="text-sm text-white flex items-center gap-1.5">
                  <Rocket size={12} className="text-[#CCFF00]" /> {t("apply.foundingTitle")}
                </div>
                <div className="text-[11px] text-white/50">
                  {t("apply.foundingSub")}
                </div>
              </div>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <Checkbox data-testid="apply-public" checked={form.opt_in_public_profile}
                        onCheckedChange={(v) => setForm({ ...form, opt_in_public_profile: !!v })} />
              <div>
                <div className="text-sm text-white">{t("apply.publicListTitle")}</div>
                <div className="text-[11px] text-white/50">
                  {t("apply.publicListSub")}
                </div>
              </div>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <Checkbox data-testid="apply-terms" checked={form.accepted_terms}
                        onCheckedChange={(v) => setForm({ ...form, accepted_terms: !!v })} />
              <div className="text-sm text-white">
                {t("apply.terms")}
              </div>
            </label>

            <Button
              type="submit"
              disabled={busy}
              data-testid="apply-submit"
              className="w-full rounded-full bg-[#CCFF00] hover:bg-[#B8E600] text-black font-semibold"
            >
              <Send size={13} className="mr-1.5" />
              {busy ? t("apply.submitting") : t("apply.submit")}
            </Button>
          </section>
        </form>
      </div>
    </PublicShell>
  );
}

/* =================================================================
 * /partners/thanks/:appId — status viewer
 * ================================================================= */
export function PartnersThanks() {
  const { t } = useTranslation();
  const { appId } = useParams();
  const [row, setRow] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get(`/partners/applications/${appId}`)
      .then((r) => setRow(r.data))
      .catch(() => setError(t("thanks.loadErr")));
  }, [appId, t]);

  if (error) {
    return (
      <PublicShell>
        <div className="max-w-xl mx-auto px-6 pt-32 pb-24 text-red-200">{error}</div>
      </PublicShell>
    );
  }
  if (!row) {
    return <PublicShell><div className="max-w-xl mx-auto px-6 pt-32 pb-24 text-white/40">{t("thanks.loading")}</div></PublicShell>;
  }

  const meta = APPLY_STATUS_META[row.status] || APPLY_STATUS_META.submitted;
  const Icon = meta.icon;

  return (
    <PublicShell>
      <SEO title="Application received" path={`/partners/thanks/${appId}`} noindex />
      <div data-testid="partners-thanks" className="max-w-xl mx-auto px-6 pt-32 pb-24">
        <div className={`border rounded-lg p-6 ${meta.tone}`}>
          <div className="flex items-center gap-2 mb-2">
            <Icon size={18} />
            <div className="text-lg font-semibold text-white">{t(meta.i18n)}</div>
          </div>
          <div className="text-sm text-white/70 leading-relaxed">
            {t("thanks.body", { name: row.name?.split(" ")[0] || "" })}
          </div>
          {row.reviewer_note && (
            <div className="mt-4 p-3 border border-white/10 rounded bg-black/20 text-xs text-white/80 leading-relaxed">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1 font-mono">
                {t("thanks.reviewerNote")}
              </div>
              {row.reviewer_note}
            </div>
          )}
          <div className="mt-5 pt-5 border-t border-white/10 flex flex-col sm:flex-row gap-3 text-xs text-white/50 font-mono">
            <span data-testid="thanks-app-id">{t("thanks.appId")} · {row.application_id?.slice(0, 8)}</span>
            <span>{t("thanks.status")} · {row.status}</span>
            <span>{(row.categories || []).join(" · ")}</span>
          </div>
        </div>
        <div className="mt-8 flex gap-3">
          <Link to="/partners"
                className="rounded-full border border-white/15 text-white hover:bg-white/5 px-5 py-2 text-sm flex items-center gap-1.5">
            <ArrowLeft size={13} /> {t("thanks.back")}
          </Link>
          <Link to="/partners/directory"
                className="rounded-full bg-[#D4B878] text-black font-semibold px-5 py-2 text-sm">
            {t("thanks.explore")} →
          </Link>
        </div>
      </div>
    </PublicShell>
  );
}

/* =================================================================
 * /partners/directory — opt-in public directory
 * ================================================================= */
const TIER_I18N = { registered: "partners.tierRegistered", silver: "partners.tierSilver", gold: "partners.tierGold", platinum: "partners.tierPlatinum" };
const TIER_TONE = {
  registered: "border-white/20 bg-white/[0.03] text-white/70",
  silver: "border-slate-300/40 bg-slate-200/[0.04] text-slate-100",
  gold: "border-[#D4B878]/50 bg-[#D4B878]/[0.08] text-[#E8D6A3]",
  platinum: "border-[#CCFF00]/50 bg-[#CCFF00]/[0.06] text-[#CCFF00]",
};

export function PartnersDirectory() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const category = params.get("category") || "";
  const country = params.get("country") || "";
  const tier = params.get("tier") || "";

  useEffect(() => {
    api.get("/partners/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const q = new URLSearchParams();
    if (category) q.set("category", category);
    if (country) q.set("country", country);
    if (tier) q.set("tier", tier);
    setLoading(true);
    api
      .get(`/partners/directory?${q.toString()}`)
      .then((r) => setItems(r.data.items || []))
      .finally(() => setLoading(false));
  }, [category, country, tier]);

  const setFilter = (k, v) => {
    const n = new URLSearchParams(params);
    if (v) n.set(k, v); else n.delete(k);
    setParams(n);
  };

  return (
    <PublicShell>
      <SEO
        title="Partner Directory"
        description="Approved, opt-in Mirrorloops partners worldwide — filter by category, country, and tier."
        path="/partners/directory"
      />
      <div data-testid="partners-directory" className="max-w-6xl mx-auto px-6 pt-32 pb-24">
        <div className="mb-8">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4B878] mb-2 flex items-center gap-2">
            <Compass size={12} /> {t("directory.eyebrow")}
          </div>
          <h1 className="text-3xl sm:text-5xl font-semibold text-white tracking-tight">
            {t("directory.title")}
          </h1>
          <p className="mt-3 text-white/60 max-w-2xl">
            {t("directory.sub")}
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          <select
            data-testid="directory-filter-category"
            value={category}
            onChange={(e) => setFilter("category", e.target.value)}
            className="bg-white/5 border border-white/10 text-xs rounded px-3 py-1.5 text-white font-mono"
          >
            <option value="">{t("directory.allCategories")}</option>
            {(meta?.categories || []).map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
          <select
            data-testid="directory-filter-tier"
            value={tier}
            onChange={(e) => setFilter("tier", e.target.value)}
            className="bg-white/5 border border-white/10 text-xs rounded px-3 py-1.5 text-white font-mono"
          >
            <option value="">{t("directory.allTiers")}</option>
            {(meta?.tiers || []).map((tier) => (
              <option key={tier.id} value={tier.id}>{tier.label}</option>
            ))}
          </select>
          <Input
            data-testid="directory-filter-country"
            placeholder={t("directory.country")}
            value={country}
            onChange={(e) => setFilter("country", e.target.value)}
            className="w-40 bg-white/5 border-white/10 text-xs"
          />
          <div className="flex-1" />
          <div className="text-xs text-white/40 font-mono flex items-center">
            {t("directory.count", { count: items.length })}
          </div>
        </div>

        {loading && <div className="text-white/40">{t("directory.loading")}</div>}
        {!loading && items.length === 0 && (
          <div data-testid="directory-empty" className="p-10 border border-dashed border-white/10 rounded text-center text-white/50">
            {t("directory.empty")}{" "}
            <Link to="/partners/apply" className="text-[#D4B878] underline">{t("directory.applyLink")}</Link>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((p) => (
            <div
              key={p.id}
              data-testid={`directory-card-${p.id}`}
              className="border border-white/10 rounded-lg p-4 bg-white/[0.02]"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="text-white font-semibold truncate">{p.name}</div>
                  <div className="text-[11px] text-white/50 font-mono flex items-center gap-1 mt-0.5">
                    <MapPin size={10} /> {p.city ? `${p.city}, ` : ""}{p.country}
                  </div>
                </div>
                <span
                  className={`text-[9px] uppercase tracking-[0.14em] font-mono px-1.5 py-0.5 rounded border ${TIER_TONE[p.tier] || TIER_TONE.registered}`}
                >
                  {TIER_I18N[p.tier] ? t(TIER_I18N[p.tier]) : p.tier}
                </span>
              </div>
              {p.public_bio && (
                <div className="text-xs text-white/60 mb-3 leading-relaxed">{p.public_bio}</div>
              )}
              <div className="flex flex-wrap gap-1">
                {(p.categories_labels || []).map((c) => (
                  <span key={c} className="text-[9px] font-mono bg-white/[0.04] border border-white/10 rounded-full px-1.5 py-0.5 text-white/60">
                    {c}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </PublicShell>
  );
}
